// ---------------------------------------------------------------------------
// Digital Notice Board — ElementVisual
// ---------------------------------------------------------------------------
// The single source of truth for how every element LOOKS. Both the editable
// design canvas and the full-screen projection player render through this, so
// "what you design is what projects". It is purely presentational: it takes an
// element + a scale factor (logical→screen px) and draws it. All interaction
// (drag/resize/select) lives in the editor wrapper, never here.
// ---------------------------------------------------------------------------
import React, {useEffect, useState} from "react";
import {Box, Flex, Image, Text} from "@chakra-ui/react";
import {ELEMENT_TYPES} from "../constants/NoticeBoardConstants";
import {resolveIcon} from "../constants/iconCatalog";
import {formatClock, formatDate} from "../utils/noticeBoardUtils";

// Ticking clock/date share one lightweight timer per element instance.
function useNow(active) {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        if (!active) return undefined;
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, [active]);
    return now;
}

export default function ElementVisual({element, scale}) {
    const s = (v) => (v || 0) * scale;
    const now = useNow(
        element.type === ELEMENT_TYPES.CLOCK || element.type === ELEMENT_TYPES.DATE
    );

    const common = {
        width: "100%",
        height: "100%",
        opacity: element.opacity != null ? element.opacity : 1,
    };

    switch (element.type) {
        case ELEMENT_TYPES.HEADING:
        case ELEMENT_TYPES.TEXT: {
            return (
                <Flex
                    {...common}
                    align="center"
                    justify={element.align === "center" ? "center" : element.align === "right" ? "flex-end" : "flex-start"}
                    bg={element.bg && element.bg !== "transparent" ? element.bg : "transparent"}
                    px={`${s(16)}px`}
                    borderRadius={`${s(8)}px`}
                    overflow="hidden"
                >
                    <Text
                        as="span"
                        fontSize={`${s(element.fontSize)}px`}
                        fontWeight={element.fontWeight}
                        fontFamily={element.fontFamily}
                        color={element.color}
                        textAlign={element.align}
                        lineHeight="1.15"
                        w="100%"
                        style={{whiteSpace: "pre-wrap", wordBreak: "break-word"}}
                    >
                        {element.text}
                    </Text>
                </Flex>
            );
        }

        case ELEMENT_TYPES.MARQUEE: {
            const dur = Math.max(3, element.speed || 12);
            return (
                <Flex {...common} align="center" bg={element.bg} overflow="hidden">
                    <Box
                        as="span"
                        whiteSpace="nowrap"
                        fontSize={`${s(element.fontSize)}px`}
                        fontWeight={element.fontWeight}
                        fontFamily={element.fontFamily}
                        color={element.color}
                        style={{
                            paddingLeft: "100%",
                            animation: `nbMarquee ${dur}s linear infinite`,
                        }}
                    >
                        {element.text}
                    </Box>
                </Flex>
            );
        }

        case ELEMENT_TYPES.NOTICE: {
            return (
                <Box
                    {...common}
                    bg={element.bg}
                    borderRadius={`${s(element.radius || 12)}px`}
                    borderLeft={`${s(10)}px solid ${element.accent}`}
                    boxShadow="0 10px 30px rgba(0,0,0,0.12)"
                    p={`${s(28)}px`}
                    overflow="hidden"
                >
                    <Text
                        fontWeight={700}
                        fontSize={`${s(element.titleSize)}px`}
                        color={element.accent}
                        fontFamily="Raleway, sans-serif"
                        mb={`${s(14)}px`}
                        noOfLines={2}
                    >
                        {element.title}
                    </Text>
                    <Text
                        fontSize={`${s(element.bodySize)}px`}
                        color={element.color}
                        fontFamily="Roboto, sans-serif"
                        lineHeight="1.3"
                        style={{whiteSpace: "pre-wrap"}}
                    >
                        {element.body}
                    </Text>
                </Box>
            );
        }

        case ELEMENT_TYPES.IMAGE: {
            if (!element.src) {
                return (
                    <Flex
                        {...common}
                        align="center"
                        justify="center"
                        bg="gray.100"
                        border="2px dashed"
                        borderColor="gray.300"
                        borderRadius={`${s(element.radius || 8)}px`}
                        color="gray.400"
                        fontSize={`${s(28)}px`}
                    >
                        Image
                    </Flex>
                );
            }
            return (
                <Image
                    {...common}
                    src={element.src}
                    objectFit={element.fit || "cover"}
                    borderRadius={`${s(element.radius || 8)}px`}
                    alt=""
                    draggable={false}
                />
            );
        }

        case ELEMENT_TYPES.ICON: {
            const Icon = resolveIcon(element.icon);
            const size = Math.min(s(element.w), s(element.h));
            return (
                <Flex {...common} align="center" justify="center" color={element.color}>
                    <Icon size={size} color={element.color} />
                </Flex>
            );
        }

        case ELEMENT_TYPES.SHAPE: {
            return (
                <Box
                    {...common}
                    bg={element.fill}
                    borderRadius={element.shape === "circle" ? "50%" : `${s(element.radius || 0)}px`}
                />
            );
        }

        case ELEMENT_TYPES.CLOCK: {
            return (
                <Flex
                    {...common}
                    align="center"
                    justify={element.align === "center" ? "center" : element.align === "right" ? "flex-end" : "flex-start"}
                >
                    <Text
                        fontSize={`${s(element.fontSize)}px`}
                        fontWeight={element.fontWeight}
                        fontFamily={element.fontFamily}
                        color={element.color}
                    >
                        {formatClock(now, element.seconds)}
                    </Text>
                </Flex>
            );
        }

        case ELEMENT_TYPES.DATE: {
            return (
                <Flex
                    {...common}
                    align="center"
                    justify={element.align === "center" ? "center" : element.align === "right" ? "flex-end" : "flex-start"}
                >
                    <Text
                        fontSize={`${s(element.fontSize)}px`}
                        fontWeight={element.fontWeight}
                        fontFamily={element.fontFamily}
                        color={element.color}
                        noOfLines={1}
                    >
                        {formatDate(now)}
                    </Text>
                </Flex>
            );
        }

        default:
            return null;
    }
}
